import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Plus, Trash2, GripVertical, Globe, Server, Cpu, Cloud, Shield, HeadphonesIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
const AVAILABLE_ICONS = [{
  name: 'Globe',
  icon: Globe
}, {
  name: 'Server',
  icon: Server
}, {
  name: 'Cpu',
  icon: Cpu
}, {
  name: 'Cloud',
  icon: Cloud
}, {
  name: 'Shield',
  icon: Shield
}, {
  name: 'HeadphonesIcon',
  icon: HeadphonesIcon
}];
interface Feature {
  title: string;
  description: string;
  iconName: string;
}
const defaultFeatures: Feature[] = [{
  title: 'Web Hosting',
  description: 'Fast, secure, and reliable shared hosting for your personal or small business websites.',
  iconName: 'Globe'
}, {
  title: 'VPS Servers',
  description: 'Dedicated resources and full root access for applications that need more power.',
  iconName: 'Server'
}, {
  title: 'Dedicated Servers',
  description: 'Bare metal performance for enterprise applications and high-traffic websites.',
  iconName: 'Cpu'
}, {
  title: 'Domain Registration',
  description: 'Find and register the perfect domain name for your brand with free privacy protection.',
  iconName: 'Cloud'
}, {
  title: 'SSL Certificates',
  description: 'Secure your website and build customer trust with our premium SSL certificates.',
  iconName: 'Shield'
}, {
  title: '24/7 Support',
  description: 'Our expert team is always awake and ready to help you with any technical issues.',
  iconName: 'HeadphonesIcon'
}];
const defaultHeader = {
  subtitle: 'Our Services',
  title: 'Everything you need to succeed online',
  description: 'We provide a comprehensive suite of tools and services designed to make your online journey fast, reliable, and hassle-free.'
};
export function ContentManager() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [headerInfo, setHeaderInfo] = useState(defaultHeader);
  const [features, setFeatures] = useState<Feature[]>(defaultFeatures);
  useEffect(() => {
    loadContent();
  }, []);
  const loadContent = async () => {
    try {
      setIsLoading(true);
      const {
        data,
        error
      } = await supabase.from('site_settings').select('key, value').in('key', ['features_content', 'features_header']);
      if (error) throw error;
      if (data) {
        const contentRow = data.find(r => r.key === 'features_content');
        if (contentRow && contentRow.value) {
          setFeatures(contentRow.value as Feature[]);
        }
        const headerRow = data.find(r => r.key === 'features_header');
        if (headerRow && headerRow.value) {
          setHeaderInfo(headerRow.value as typeof defaultHeader);
        }
      }
    } catch (error) {
      console.error('Failed to load content:', error);
      toast.error('Failed to load content settings');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updates = [{
        key: 'features_header',
        value: headerInfo
      }, {
        key: 'features_content',
        value: features
      }];
      const {
        error
      } = await supabase.from('site_settings').upsert(updates, {
        onConflict: 'key'
      });
      if (error) throw error;
      toast.success('Content saved successfully');
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error('Failed to save content settings');
    } finally {
      setIsSaving(false);
    }
  };
  const addFeature = () => {
    setFeatures([...features, {
      title: 'New Feature',
      description: 'Feature description',
      iconName: 'Globe'
    }]);
  };
  const removeFeature = (index: number) => {
    const newFeatures = [...features];
    newFeatures.splice(index, 1);
    setFeatures(newFeatures);
  };
  const moveFeature = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === features.length - 1) return;
    const newFeatures = [...features];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newFeatures[index];
    newFeatures[index] = newFeatures[targetIndex];
    newFeatures[targetIndex] = temp;
    setFeatures(newFeatures);
  };
  const updateFeature = (index: number, field: keyof Feature, value: string) => {
    const newFeatures = [...features];
    newFeatures[index] = {
      ...newFeatures[index],
      [field]: value
    };
    setFeatures(newFeatures);
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-id="element-95">
        <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-96" />
      </div>;
  }
  return <div className="space-y-8" data-id="element-97">
      <div className="flex items-center justify-between" data-id="element-98">
        <div data-id="element-99">
          <h1 className="text-2xl font-bold text-white mb-2" data-id="element-100">
            Content Manager
          </h1>
          <p className="text-slate-400" data-id="element-101">
            Manage your website's homepage content and features.
          </p>
        </div>
        <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-6 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-102">
          {isSaving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-103" /> : <Save size={20} data-id="element-104" />}
          Save Changes
        </button>
      </div>

      {/* Header Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6" data-id="element-105">
        <h2 className="text-lg font-bold text-white mb-6" data-id="element-106">Features Header</h2>
        <div className="space-y-4" data-id="element-107">
          <div data-id="element-108">
            <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-109">
              Subtitle
            </label>
            <input type="text" value={headerInfo.subtitle} onChange={e => setHeaderInfo({
            ...headerInfo,
            subtitle: e.target.value
          })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 transition-colors" placeholder="e.g., Our Services" data-id="element-110" />
          </div>
          <div data-id="element-111">
            <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-112">
              Title
            </label>
            <input type="text" value={headerInfo.title} onChange={e => setHeaderInfo({
            ...headerInfo,
            title: e.target.value
          })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 transition-colors" placeholder="e.g., Everything you need to succeed online" data-id="element-113" />
          </div>
          <div data-id="element-114">
            <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-115">
              Description
            </label>
            <textarea value={headerInfo.description} onChange={e => setHeaderInfo({
            ...headerInfo,
            description: e.target.value
          })} rows={3} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 transition-colors resize-none" placeholder="Enter description..." data-id="element-116" />
          </div>
        </div>
      </div>

      {/* Features List */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6" data-id="element-117">
        <div className="flex items-center justify-between mb-6" data-id="element-118">
          <h2 className="text-lg font-bold text-white" data-id="element-119">Feature Cards</h2>
          <button onClick={addFeature} className="flex items-center gap-2 text-sm font-medium text-fox-500 hover:text-fox-400 transition-colors" data-id="element-120">
            <Plus size={16} data-id="element-121" />
            Add Feature
          </button>
        </div>

        <div className="space-y-4" data-id="element-122">
          {features.map((feature, index) => <motion.div key={index} layout initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex gap-4" data-id="element-123">
              <div className="flex flex-col items-center justify-center gap-2 text-slate-500 border-r border-slate-800 pr-4" data-id="element-124">
                <button onClick={() => moveFeature(index, 'up')} disabled={index === 0} className="hover:text-white disabled:opacity-30 disabled:hover:text-slate-500 transition-colors" data-id="element-125">
                  <ArrowUp size={18} data-id="element-126" />
                </button>
                <GripVertical size={18} className="opacity-50" data-id="element-127" />
                <button onClick={() => moveFeature(index, 'down')} disabled={index === features.length - 1} className="hover:text-white disabled:opacity-30 disabled:hover:text-slate-500 transition-colors" data-id="element-128">
                  <ArrowDown size={18} data-id="element-129" />
                </button>
              </div>

              <div className="flex-1 space-y-4" data-id="element-130">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-131">
                  <div data-id="element-132">
                    <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-133">
                      Title
                    </label>
                    <input type="text" value={feature.title} onChange={e => updateFeature(index, 'title', e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fox-500 transition-colors" data-id="element-134" />
                  </div>
                  <div data-id="element-135">
                    <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-136">
                      Icon
                    </label>
                    <div className="relative" data-id="element-137">
                      <select value={feature.iconName} onChange={e => updateFeature(index, 'iconName', e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fox-500 transition-colors appearance-none" data-id="element-138">
                        {AVAILABLE_ICONS.map(icon => <option key={icon.name} value={icon.name} data-id="element-139">
                            {icon.name}
                          </option>)}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-slate-400" data-id="element-140">
                        <ArrowDown size={14} data-id="element-141" />
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-142">
                  <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-143">
                    Description
                  </label>
                  <input type="text" value={feature.description} onChange={e => updateFeature(index, 'description', e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fox-500 transition-colors" data-id="element-144" />
                </div>
              </div>

              <div className="flex items-center border-l border-slate-800 pl-4" data-id="element-145">
                <button onClick={() => removeFeature(index)} className="text-slate-500 hover:text-red-500 transition-colors p-2" data-id="element-146">
                  <Trash2 size={20} data-id="element-147" />
                </button>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}